import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rm } from "@/lib/format";
import { useDashboardCtx } from "@/context/DashboardContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Stethoscope, Sparkles } from "lucide-react";

const SCORE_LABELS = { performance: "Performance", lead_quality: "Lead Quality", sales_conversion: "Sales Conversion", creative: "Creative", tracking_health: "Tracking Health", compliance_risk: "Compliance Risk" };

function scoreColor(key, val) {
  if (key === "compliance_risk") return val < 20 ? "text-accent" : val < 40 ? "text-primary" : "text-destructive";
  return val >= 75 ? "text-accent" : val >= 50 ? "text-primary" : "text-destructive";
}

export default function CampaignDoctor() {
  const { customerId } = useDashboardCtx();
  const [camps, setCamps] = useState(null);
  const [selected, setSelected] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/campaigns", { params: customerId ? { customer_id: customerId } : {} })
      .then((r) => setCamps(r.data)).catch(() => {});
  }, [customerId]);

  const diagnose = async (c) => {
    setSelected(c.id); setDiag(null); setLoading(true);
    try { const r = await api.post("/ai/campaign-doctor", { campaign_id: c.id }); setDiag(r.data); }
    catch (e) { toast.error("Diagnosis failed"); }
    finally { setLoading(false); }
  };

  if (!camps) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 animate-float-up" data-testid="doctor-panel">
      {camps.map((c) => (
        <div key={c.id} data-testid={`doctor-campaign-${c.id}`} className="bg-card border border-border rounded-2xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${c.platform === "Meta" ? "bg-chart-3/15 text-chart-3" : "bg-primary/15 text-primary"}`}>{c.platform}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${c.status === "Active" ? "bg-accent/15 text-accent" : "bg-muted text-muted-foreground"}`}>{c.status}</span>
              </div>
              <p className="font-display font-semibold mt-2">{c.name}</p>
              <p className="text-xs text-muted-foreground">Spend {rm(c.spend)} · {c.leads} leads · {c.qualified} qualified · {c.sales} sales · {c.roas}x ROAS</p>
            </div>
            <Button data-testid={`doctor-diagnose-${c.id}`} onClick={() => diagnose(c)} disabled={loading && selected === c.id} className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shrink-0">
              {loading && selected === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Stethoscope className="w-4 h-4 mr-1" /> Diagnose</>}
            </Button>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
            {Object.entries(c.scores).map(([k, v]) => (
              <div key={k} className="bg-secondary/30 rounded-lg p-2.5 text-center">
                <p className={`font-display text-lg font-bold ${scoreColor(k, v)}`}>{v}</p>
                <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{SCORE_LABELS[k]}</p>
              </div>
            ))}
          </div>

          {selected === c.id && diag && (
            <div className="mt-4 border-t border-border pt-4 space-y-3 animate-float-up" data-testid={`doctor-result-${c.id}`}>
              <p className="text-sm"><Sparkles className="w-3.5 h-3.5 inline text-primary mr-1" />{diag.summary}</p>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Diagnosis</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground">{(diag.diagnosis || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
              </div>
              {diag.root_cause && <p className="text-sm"><b className="text-primary">Root cause:</b> {diag.root_cause}</p>}
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Recommended Actions</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground">{(diag.recommendations || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
